/**
 * Impersonation session store.
 *
 * When the super_admin starts impersonation via `POST /admin/tenants/:id/impersonate`,
 * the returned JWT is stashed here and sent (instead of the normal access
 * token) on every subsequent API call until `exit()` is called. The store
 * persists to localStorage so a page refresh keeps the impersonation session
 * alive until the JWT's 15-minute TTL expires.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface ImpersonationState {
  /** HS256 JWT issued by the impersonation endpoint. */
  accessToken: string | null
  /** Tenant being impersonated. */
  tenantId: string | null
  /** Optional display slug for the banner. */
  tenantSlug: string | null
  /** The super_admin email — displayed in the banner. */
  adminEmail: string | null
  /** ISO8601 expiry timestamp. */
  expiresAt: string | null

  start: (args: {
    accessToken: string
    tenantId: string
    tenantSlug: string
    adminEmail: string
    expiresAt: string
  }) => void
  exit: () => void
  isActive: () => boolean
}

export const useImpersonationStore = create<ImpersonationState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      tenantId: null,
      tenantSlug: null,
      adminEmail: null,
      expiresAt: null,

      start: (args) =>
        set({
          accessToken: args.accessToken,
          tenantId: args.tenantId,
          tenantSlug: args.tenantSlug,
          adminEmail: args.adminEmail,
          expiresAt: args.expiresAt,
        }),

      exit: () =>
        set({
          accessToken: null,
          tenantId: null,
          tenantSlug: null,
          adminEmail: null,
          expiresAt: null,
        }),

      isActive: () => {
        const s = get()
        if (s.accessToken === null || s.expiresAt === null) return false
        return new Date(s.expiresAt).getTime() > Date.now()
      },
    }),
    {
      name: 'leviathan-impersonation',
      partialize: (s) => ({
        accessToken: s.accessToken,
        tenantId: s.tenantId,
        tenantSlug: s.tenantSlug,
        adminEmail: s.adminEmail,
        expiresAt: s.expiresAt,
      }),
    }
  )
)

/** Convenience: hook-style accessor used by components. */
export function useImpersonation(): Pick<
  ImpersonationState,
  'accessToken' | 'tenantId' | 'tenantSlug' | 'adminEmail' | 'expiresAt' | 'start' | 'exit'
> & { active: boolean } {
  const state = useImpersonationStore()
  return {
    accessToken: state.accessToken,
    tenantId: state.tenantId,
    tenantSlug: state.tenantSlug,
    adminEmail: state.adminEmail,
    expiresAt: state.expiresAt,
    start: state.start,
    exit: state.exit,
    active:
      state.accessToken !== null &&
      state.expiresAt !== null &&
      new Date(state.expiresAt).getTime() > Date.now(),
  }
}
